import React, { memo, useCallback } from 'react';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { BillsListBloc } from './BillsListBloc';
import { useBloc } from '@core/utils/bloc';
import { Formatter } from '@core/utils/formatter';
import { EmptySection } from '@core/components/EmptySection';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { formControl, FormDataType, formGroup } from '@core/utils/form';
import { TMProviderInvoiceResponse } from '@business/entities/tm/TMProviderInvoiceResponse';
import { CBody } from '@modules/common/CBody';
import { HasPermission } from '@core/components/HasPermission';
import { Button } from '@core/components/Button';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { tapError } from '@core/utils/rxjs/operators';
import { TableIcon } from '@modules/common/TableActions';
import { openViewProviderBillModal } from '../ViewProviderBill';
import { PaymentType } from '@business/entities/tm';
import { TimeMaterialService } from '@business/services/time_material_service';

export const defineBillsFilterForm = () => {
  return formGroup({
    providerId: formControl<string>({ placeholder: 'Select Vendor' }),
    status: formControl<string>({ placeholder: 'Select Status' }),
  });
};

export type BillsFilterFormData = FormDataType<typeof defineBillsFilterForm>;
export type BillsFilterFormType = ReturnType<typeof defineBillsFilterForm>;

export interface BillsListViaTypeProps {
  type: PaymentType | 'all';
  bloc: BillsListBloc;
}

export const BillsListViaType = memo((props: BillsListViaTypeProps) => {
  const { type, bloc } = props;

  const items = useNonNilObservable(bloc.items$);
  const providers = useNonNilObservable(bloc.providers$);

  const columns: GCColumn<TMProviderInvoiceResponse>[] = [
    {
      name: 'Vendor',
      sortField: 'provider.name',
      sortable: true,
      cell: v => v.provider.name,
    },
    {
      name: 'Invoice Date',
      sortField: 'date',
      width: '10%',
      sortable: true,
      cell: v => Formatter.formatTime(v.date),
    },
    {
      name: 'QBO Handle/ Name',
      sortField: 'w9',
      width: '20%',
      sortable: true,
      cell: v => v.provider.w9 ?? v.provider.name,
    },
    {
      name: 'Amount',
      sortField: 'amount',
      sortable: true,
      cell: v => Formatter.formatCurrency(v.amount, 'USD'),
    },
    {
      name: 'Reference',
      sortField: 'refNo',
      sortable: true,
      cell: v => v.refNo,
    },
    {
      name: 'Created At',
      sortField: 'createdAt',
      width: '10%',
      sortable: true,
      cell: v => Formatter.formatTime(v.createdAt),
    },
    {
      name: 'Action',
      width: '12%',
      cell: v => <SyncInvoice invoice={v} bloc={bloc} />,
    },
  ];

  return (
    <EmptySection
      items={items}
      icon="bx bx-receipt"
      title={`No Bills for ${TimeMaterialService.getPaymentTypeStatusTitle(type)}`}
      description={"You don't have any bills created yet. Bills will appear here once you create them from payables."}
    >
      <GCTable
        noSearch
        noHeader
        sortServer
        columns={columns}
        data={items}
        paginated={bloc.paginated}
        paginationRowsPerPageOptions={[25, 50, 100]}
        noQueryOnSearch
      />
    </EmptySection>
  );
});

interface SyncInvoiceActionProps {
  invoice: TMProviderInvoiceResponse;
  bloc: BillsListBloc;
}

export const SyncInvoice = memo((props: SyncInvoiceActionProps) => {
  const { invoice, bloc } = props;

  const [isSyncing, setIsSyncing] = React.useState(false);

  const onViewBill = useCallback(() => {
    openViewProviderBillModal({ billId: invoice.id, onDone: () => bloc.paginated.reload().subscribe() });
  }, [invoice]);

  const onSync = useCallback(() => {
    setIsSyncing(true);
    of(null)
      .pipe(
        switchMap(() => bloc.syncInvoiceToQBO(invoice.id)),
        tap(() => bloc.paginated.reload().subscribe()),
        tap(() => setIsSyncing(false)),
        tapError(() => setIsSyncing(false)),
      )
      .subscribe();
  }, [bloc, invoice.id, setIsSyncing]);

  const onReSync = useCallback(() => {
    setIsSyncing(true);
    of(null)
      .pipe(
        switchMap(() => bloc.reSyncInvoiceToSystem(invoice.id)),
        tap(() => bloc.paginated.reload().subscribe()),
        tap(() => setIsSyncing(false)),
        tapError(() => setIsSyncing(false)),
      )
      .subscribe();
  }, [bloc, invoice.id, setIsSyncing]);

  return (
    <div className="d-flex">
      <HasPermission module={'manage-qbo-provider-invoice-sync'} moduleKey={'manage-qbo-provider-invoice-sync'}>
        <TableIcon tooltip={'Click to view the bill details'} onClick={onViewBill} className="mdi mdi-eye font-size-16 text-dark" />
        {!!invoice.isTmNotLinked && (
          <TableIcon
            tooltip={'This bill has items not linked to any work order. Click to view details.'}
            className="fa fa-exclamation-triangle font-size-16 text-warning"
            onClick={onViewBill}
          />
        )}
        {invoice.isOutOfSync ? (
          <Button intent="warning" size="small" loading={isSyncing} onClick={onReSync}>
            Out of Sync
          </Button>
        ) : (
          <Button disabled={!!invoice.qbSyncedDate} size="small" loading={isSyncing} onClick={onSync}>
            {invoice.qbSyncedDate ? 'Synced' : 'Sync to QBO'}
          </Button>
        )}
      </HasPermission>
    </div>
  );
});
